import { HeadCellType, OptionQueryType } from '@components/Table/types';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { generateOption, getNestedValue } from '@utils/utils';
import { styleNoUpperCase } from '../../../context/utils';
import { FieldMapping, FieldValueType, FileFieldTypesMap, MappingTreeNode, MappingType } from './types';

export const itemStyle = (isSmall?: boolean) => ({
  ...flex.itemsCenter,
  gap: 1,
  flexShrink: 0,
  width: isSmall ? 266 : 282,
  height: isSmall ? 32 : 40,
  fontSize: isSmall ? 13 : 14,
  background: theme => theme.palette.white.main,
  px: 2,
  borderRadius: '2px',
  svg: {
    color: theme => theme.palette.colors.primary[500],
    width: isSmall ? 18 : undefined,
    height: isSmall ? 18 : undefined,
  },
  span: ellipsis,
  transition: theme => theme.transitions.create(['box-shadow']),
  ':hover': {
    boxShadow: '0px 15px 20px rgba(0, 0, 0, 0.08)',
  },
});

export const buildMappingKey = (item: Partial<FieldMapping>) =>
  `${item.entityName}.${item.modelFieldName}.replica-${item.replicaNumber ?? 0}`;

export const isScriptOrLiteral = (mappingType: MappingType) => [MappingType.Script, MappingType.Literal].includes(mappingType);

export const getEntitiesShouldBeOpen = (mappings: FieldMapping[]) =>
  mappings.reduce((obj, entity) => ({ ...obj, [entity.entityName]: true }), {});

export const getMapRowIndex = (mappings: FieldMapping[], key: string) => mappings.findIndex(item => buildMappingKey(item) === key);

export const createField = (label: string, isMissing: boolean): MappingTreeNode => ({ label, isMissing, path: label, children: [] });

export const getMaxReplica = (mappings: FieldMapping[]) =>
  mappings.reduce((maxReplica, { replicaNumber }) => Math.max(replicaNumber ?? 0, maxReplica), 0);

export const generateNestedSqlAccessor = (path: string, rootObject = 'rawlogs.DOCUMENT'): string => {
  if (!path) {
    return rootObject;
  }

  const pathSegments = path.split('.');

  return pathSegments.reduce((currentExpression, segment) => `object_get(${currentExpression},'${segment}')`, rootObject);
};

export const formatExplodedFieldLabel = (field?: string, isExploded?: boolean) => field + (isExploded ? ' exploded' : '');

export const formatExplodedFieldValue = (field = '', isExploded = false) => JSON.stringify({ mappingValue: field, exploded: isExploded });

export const parseExplodedField = (value: string): { mappingValue: string; exploded: boolean } => {
  const defaultField = { mappingValue: value, exploded: false };
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null) {
      return { mappingValue: parsed.mappingValue, exploded: parsed.exploded };
    }
  } catch {
    return defaultField;
  }

  return defaultField;
};

type BaseMappingItem = Omit<FieldMapping, 'fieldValueType' | 'mappingValue' | 'compositeValue' | 'exploded'>;

export const createSimpleFieldMapping = (baseItem: BaseMappingItem, mappingValue: string, mappingExploded?: boolean): FieldMapping =>
  ({
    ...baseItem,
    exploded: mappingExploded,
    fieldValueType: FieldValueType.MappingValue,
    mappingValue,
  }) as FieldMapping;

export const createCompositeFieldMapping = (baseItem: BaseMappingItem, mappingValue: string[]): FieldMapping =>
  ({
    ...baseItem,
    fieldValueType: FieldValueType.CompositeValue,
    compositeValue: {
      fields: mappingValue.map(
        v =>
          ({
            ...baseItem,
            fieldValueType: FieldValueType.MappingValue,
            ...parseExplodedField(v),
          }) as FieldMapping
      ),
    },
  }) as FieldMapping;

export const extractPropertiesFromScript = (script: string): string[] => {
  const regex = /row(?:\[['"]([^'"]+)['"]\]|\.get\(\s*['"]([^'"]+)['"](?:,\s*[^)]*)?\))/g;

  const allMatches = Array.from(script.matchAll(regex), match => match[1] || match[2]);

  return Array.from(new Set(allMatches)).sort();
};

export const getHeadCells = ({
  headers,
  instanceId,
  lastWfRun,
  headerTypes = {},
}: {
  headers: string[];
  instanceId?: string;
  lastWfRun?: string;
  headerTypes?: FileFieldTypesMap;
}): HeadCellType[] => [
  { id: 'avalor_unique_key', isKey: true, hidden: true },
  ...headers.map(id => ({
    id,
    label: id,
    formatter: (val, row) => {
      const value = getNestedValue(id, row);

      return typeof value === 'object' || typeof value === 'boolean' ? JSON.stringify(value) : value;
    },
    type: headerTypes[id],
    sx: styleNoUpperCase,
    asyncGetOptions:
      instanceId && lastWfRun
        ? (search: string) => ({
            type: OptionQueryType.SQL,
            key: `options${id}${search}`,
            sql: `SELECT distinct(${generateNestedSqlAccessor(id)}) as value
                          FROM rawlogs
                          WHERE AVALOR_CONTEXT(rawlogs
                              , 'workflowId') = '${lastWfRun}'
                            AND rawlogs.DSI_ID = '${instanceId}' ${search ? `AND value LIKE '%${search}%'` : ''}
                          ORDER BY value DESC
                          OFFSET 0 LIMIT 100`,
            onSuccess: d => d.map(r => generateOption(typeof r.value === 'object' ? JSON.stringify(r.value) : r.value)),
          })
        : undefined,
  })),
];

export const getNextLevelFieldGroupName = (groupName: string, itemsCount: number) => `${groupName} Level ${itemsCount + 1}`;

export const isMappingExploded = (mapping: FieldMapping) =>
  (mapping.fieldValueType === FieldValueType.MappingValue && !!mapping.exploded) ||
  (mapping.fieldValueType === FieldValueType.CompositeValue && !!mapping.compositeValue?.fields?.some(f => f.exploded));
